import { useState } from 'react'
import { toast } from 'sonner'
import {
  PURPLE,
  PURPLE_MID,
  PURPLE_LIGHT,
  EVENT_TYPES,
  DISTANCES,
  getCityTier,
  isAreaPlace,
  isBroaderThanCity,
  detectCityFromPlace,
  getLocationBoundaryValidationError,
  getVenueGroupNameForLocation,
} from '~/client/components/recs/constants'
import { UnsupportedCityAlert } from '~/client/components/recs/UnsupportedCityAlert'
import { useCreateEvent } from '~/client/event/use-create-event.hook'
import { useAuthStore } from '~/client/common/stores/auth'
import {
  GoogleMapsAutocomplete,
  useGoogleMapsAutocomplete,
} from '~/client/components/ui/google-maps-autocomplete'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/client/components/ui/select'
import { useNavigate } from '@tanstack/react-router'
import type { CreatedEventPayload } from './CreateEventStep'

type Props = {
  onCancel: () => void
  onCreate?: (payload: CreatedEventPayload) => void
  spaceId?: number
  spaceIds?: number[]
  shouldAddSpaceOnCreate?: boolean
}

const DISTANCE_LABELS: Record<string, string> = {
  '0.5 mi': '~10 min walk',
  '1 mi': '',
  '5 mi': '',
  '10 mi': '',
}

function distanceToMiles(d: string): number {
  const n = parseFloat(d)
  return isNaN(n) ? 1 : n
}

export function CreateEventModalForm({ onCancel, onCreate, spaceId, spaceIds, shouldAddSpaceOnCreate = false }: Props) {
  const createEventMutation = useCreateEvent()
  const userDetail = useAuthStore((s) => s.userDetail)
  const venueGroups = userDetail?.active_entity?.venue_groups ?? []
  const entityCityNames = venueGroups.map((vg: any) => vg.name as string)

  const { value: locationInput, onChange: handleGoogleChange, placeDetails } = useGoogleMapsAutocomplete()
  const [detectedCity, setDetectedCity] = useState<string | null>(null)
  const [cityStatus, setCityStatus] = useState<'supported' | 'unsupported' | 'too_broad' | null>(null)
  const [distance, setDistance] = useState('0.5 mi')
  const [isLocationArea, setIsLocationArea] = useState(false)
  const [guests, setGuests] = useState('')
  const [eventType, setEventType] = useState('')
  const [eventName, setEventName] = useState('')
  const [formTouched, setFormTouched] = useState(false)
  const [guestsError, setGuestsError] = useState<string | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const navigate = useNavigate()

  const matchedVenueGroupId = detectedCity
    ? venueGroups.find((vg: any) => vg.name.toLowerCase() === detectedCity.toLowerCase())?.id
    : undefined

  const isSupported = cityStatus === 'supported'
  const isUnsupported = cityStatus === 'unsupported'
  const isToooBroad = cityStatus === 'too_broad'
  const formReady = isSupported && guests && !guestsError && eventType && eventName.trim()

  const handlePlaceSelect = (pd: any) => {
    // Reject places broader than a city (states, countries, etc.)
    if (isBroaderThanCity(pd?.types)) {
      setDetectedCity(null)
      setCityStatus('too_broad')
      setIsLocationArea(true)
      return
    }
    const lat = pd?.geometry?.location.lat()
    const lng = pd?.geometry?.location.lng()
    const boundaryMatchedCity = getVenueGroupNameForLocation({ lat, lng, venueGroups })
    const nameMatchedCity = detectCityFromPlace(pd)
    const matchedCity = boundaryMatchedCity ?? nameMatchedCity
    if (matchedCity) {
      setDetectedCity(matchedCity)
      setCityStatus('supported')
    } else {
      setDetectedCity(null)
      setCityStatus('unsupported')
    }
    setIsLocationArea(isAreaPlace(pd?.types))
  }

  const handleLocationInputChange = (value: string, pd?: any) => {
    handleGoogleChange(value, pd)
    if (!pd) {
      setDetectedCity(null)
      setCityStatus(null)
      setIsLocationArea(false)
    }
  }

  const resetLocation = () => {
    handleGoogleChange('')
    setDetectedCity(null)
    setCityStatus(null)
    setIsLocationArea(false)
  }

  const fieldStyle = (hasValue: boolean, isInvalid?: boolean): React.CSSProperties => ({
    width: '100%',
    padding: '10px 12px',
    border: `1px solid ${isInvalid ? '#FCA5A5' : hasValue ? '#C4B5FD' : '#E5E7EB'}`,
    borderRadius: 8,
    fontSize: 14,
    fontFamily: 'inherit',
    color: '#111',
    background: '#fff',
    boxSizing: 'border-box' as const,
    transition: 'all 0.15s',
    appearance: 'none' as const,
    WebkitAppearance: 'none' as const,
  })

  const handleCreateEvent = async () => {
    if (!formReady) {
      setFormTouched(true)
      return
    }

    const city = detectedCity || 'Chicago'
    const tier = getCityTier(city)

    if (!tier) return

    // Capture lat/lng before any async work (Google Maps objects may be GC'd)
    const lat = placeDetails?.geometry?.location.lat() ?? undefined
    const lng = placeDetails?.geometry?.location.lng() ?? undefined

    const locationBoundaryError = getLocationBoundaryValidationError({ lat, lng, venueGroups })
    if (locationBoundaryError) {
      toast.error(locationBoundaryError, { position: 'bottom-right' })
      setFormTouched(true)
      return
    }

    setIsCreating(true)

    try {
      const result = await createEventMutation.mutateAsync({
        payload: {
          name: eventName.trim(),
          location: locationInput,
          location_max_distance: isLocationArea ? null : distanceToMiles(distance),
          latitude: lat ?? null,
          longitude: lng ?? null,
          num_attendees: parseInt(guests, 10) || null,
          event_type: eventType || null,
          privacy_requirement: null,
          support_mode: tier === 'essential' ? 'revel_recs' : 'self_service',
          venue_group_id: matchedVenueGroupId ?? null,
        },
        spaceId: shouldAddSpaceOnCreate && typeof spaceId === 'number' ? spaceId : undefined,
        spaceIds: shouldAddSpaceOnCreate && spaceIds && spaceIds.length > 0 ? spaceIds : undefined,
        shouldAddSpaceOnCreate,
      })

      const createdEventId = result.event?.id
      const createdSlug = result.event?.slug

      if (!createdEventId) {
        toast.error('Failed to create event. Please try again.')
        setIsCreating(false)
        return
      }

      // Build search params with all filter data baked in, then navigate.
      const slug = createdSlug || String(createdEventId)
      onCreate?.({
        eventId: Number(createdEventId),
        eventName: eventName.trim(),
      })

      const guestsNum = parseInt(guests, 10) || undefined
      let standingGuestCount: number | undefined
      let seatedGuestCount: number | undefined
      if (guestsNum && eventType) {
        if (eventType === 'Cocktail Reception + Dinner') {
          seatedGuestCount = Math.round(guestsNum * 1.75)
        } else if (['Cocktail Reception', 'Happy Hour', 'Holiday Party'].includes(eventType)) {
          standingGuestCount = guestsNum
        } else if (['Dinner', 'Meeting', 'Meeting with Breakout Spaces', 'Breakfast', 'Lunch'].includes(eventType)) {
          seatedGuestCount = guestsNum
        }
      }

      // Navigate to the new event page — the slug-change effect in
      // events.$eventSlug.tsx will resetFilters() once the new slug renders,
      // avoiding a race where the old page's filter-sync effect overwrites
      // this navigation with stale search params.
      navigate({
        to: '/events/$eventSlug',
        params: { eventSlug: slug },
        search: {
          fromCreate: true,
          location: locationInput || undefined,
          locationLat: lat,
          locationLng: lng,
          radiusMiles: isLocationArea ? undefined : distanceToMiles(distance),
          standingGuestCount,
          seatedGuestCount,
        },
      })
      // Close modal AFTER navigation starts (not before)
      onCancel()
    } catch (err) {
      console.error('Event creation failed:', err)
      toast.error('Something went wrong creating your event.')
      setIsCreating(false)
    }
  }

  return (
    <div className="space-y-5">
      {/* Location */}
      <div>
        <label className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold tracking-tight text-gray-700">
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#9CA3AF"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          Find a venue near
        </label>
        <GoogleMapsAutocomplete
          value={locationInput}
          onChange={handleLocationInputChange}
          onPlaceSelect={handlePlaceSelect}
          placeholder="(e.g. Javits Center, 375 Park Ave)"
          className={`recs-input !h-auto !rounded-lg !border !px-3 !py-2.5 !pl-9 !text-sm ${isUnsupported || isToooBroad
            ? '!border-red-300'
            : isSupported
              ? '!border-violet-300'
              : '!border-gray-200'
            }`}
        />

        {isSupported && detectedCity && (
          <div
            className="mt-1.5 inline-flex items-center gap-1 rounded-full px-[9px] py-[3px] text-[11px] font-semibold"
            style={{
              background: PURPLE_LIGHT,
              color: PURPLE,
              animation: 'recs-fadeIn 0.2s ease both',
            }}
          >
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none">
              <polyline
                points="20 6 9 17 4 12"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {detectedCity}
          </div>
        )}

        {isToooBroad && (
          <div
            className="mt-2 rounded-[10px] border border-orange-200 bg-orange-50 px-4 py-3.5"
            style={{ animation: 'recs-fadeIn 0.2s ease both' }}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="mb-0.5 text-[13px] font-semibold text-orange-900">
                  That's a bit too broad
                </div>
                <div className="text-xs leading-relaxed text-orange-700">
                  Please enter a specific city or address so we can find nearby venues.
                </div>
              </div>
              <button
                onClick={resetLocation}
                className="border-none bg-transparent text-lg leading-none text-orange-700 hover:opacity-70"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {isUnsupported && (
          <UnsupportedCityAlert
            cityName={locationInput.split(',')[0]?.trim()}
            onDismiss={resetLocation}
            entityCityNames={entityCityNames}
          />
        )}

        {/* Within (distance chips) — only shown for POI, not city/area */}
        {isSupported && !isLocationArea && (
          <div className="mt-3">
            <label className="mb-1.5 block text-xs font-semibold tracking-tight text-gray-700">
              Within
            </label>
            <div className="flex gap-1.5">
              {DISTANCES.map((d) => (
                <button
                  key={d}
                  onClick={() => setDistance(d)}
                  className="cursor-pointer whitespace-nowrap rounded-lg px-3 py-2 font-inherit text-xs font-medium leading-tight transition-all duration-150"
                  style={{
                    border: `1px solid ${distance === d ? PURPLE : '#E5E7EB'}`,
                    background: distance === d ? PURPLE_LIGHT : '#fff',
                    color: distance === d ? PURPLE : '#6B7280',
                  }}
                >
                  {d}
                  {DISTANCE_LABELS[d] && (
                    <>
                      <br />
                      <span className="text-[10px] opacity-70">
                        {DISTANCE_LABELS[d]}
                      </span>
                    </>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* # of Guests + Type of Event */}
      <div className="grid grid-cols-[140px_1fr] gap-4">
        <div>
          <label className="mb-1.5 block text-xs font-semibold tracking-tight text-gray-700">
            # of Guests
          </label>
          <input
            type="text"
            inputMode="numeric"
            value={guests}
            onChange={(e) => {
              const val = e.target.value
              setGuests(val)
              if (val && !/^\d+$/.test(val.trim())) {
                setGuestsError('Please enter a whole number')
              } else if (val && parseInt(val.trim(), 10) < 1) {
                setGuestsError('Must be at least 1')
              } else {
                setGuestsError(null)
              }
            }}
            placeholder="e.g. 50"
            className="recs-input"
            style={fieldStyle(!!guests, !!guestsError)}
          />
          {guestsError && (
            <div className="mt-1 text-[11px] text-red-500">{guestsError}</div>
          )}
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold tracking-tight text-gray-700">
            Type of Event
          </label>
          <Select value={eventType} onValueChange={setEventType}>
            <SelectTrigger
              className={`!h-auto !rounded-lg !py-2.5 !text-sm ${eventType ? '!border-violet-300' : '!border-gray-200'
                }`}
            >
              <SelectValue placeholder="Select event type" />
            </SelectTrigger>
            <SelectContent>
              {EVENT_TYPES.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Event Name */}
      <div>
        <label className="mb-1.5 block text-xs font-semibold tracking-tight text-gray-700">
          Event Name
        </label>
        <input
          value={eventName}
          onChange={(e) => {
            setEventName(e.target.value)
            setFormTouched(true)
          }}
          placeholder="e.g. Q4 Leadership Dinner"
          className="recs-input"
          style={fieldStyle(!!eventName, formTouched && !eventName.trim())}
        />
        {formTouched && !eventName.trim() && (
          <div className="mt-1 text-[11px] text-red-500">Event name is required</div>
        )}
      </div>

      {/* CTA */}
      <div className="flex justify-end">
        <button
          onClick={handleCreateEvent}
          disabled={isCreating}
          className="rounded-[9px] border-none px-7 py-[11px] font-inherit text-sm font-semibold transition-all duration-150"
          style={{
            background: isCreating ? PURPLE_MID : formReady ? PURPLE : PURPLE_LIGHT,
            color: formReady || isCreating ? '#fff' : '#C4B5FD',
            cursor: formReady && !isCreating ? 'pointer' : 'default',
            opacity: isCreating ? 0.8 : 1,
          }}
          onMouseEnter={(e) => {
            if (formReady && !isCreating) {
              e.currentTarget.style.background = PURPLE_MID
              e.currentTarget.style.boxShadow = '0 4px 14px rgba(107,33,232,0.3)'
            }
          }}
          onMouseLeave={(e) => {
            if (!isCreating) {
              e.currentTarget.style.background = formReady ? PURPLE : PURPLE_LIGHT
              e.currentTarget.style.boxShadow = 'none'
            }
          }}
        >
          {isCreating ? (
            <span className="flex items-center gap-2">
              <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
              Creating event...
            </span>
          ) : (
            'Create event'
          )}
        </button>
      </div>
    </div>
  )
}
